import { FC, useEffect, useMemo, useState } from 'react';
import { Control, useForm, Validate } from 'react-hook-form';

import { parseValidEnumeration } from '@/components/search/v1/utils/searchValidations';
import { defaultFilter, type SignalenFilter } from '@/components/sidebar/signalen/signalenFilter/signalenFilterModel';
import { useSignaalTypenSearch } from '@/components/sidebar/signalen/signalenFilter/useSignaalTypenSearch';
import SortField from '@/components/sidebar/sortField/SortField';
import {
  getSortSignalenOptions,
  resolveSorting,
  SorteringRichting,
  type SorteringType,
  useSorting,
} from '@/components/sidebar/sortField/useSorting';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { CreatableSelectField } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import { InputField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import DienstregelpuntSelectField from '@/components/UI/Fields/ReactFormFields/DienstregelpuntSelectField';
import MaterieelTypeSelectField from '@/components/UI/Fields/ReactFormFields/MaterieelTypeSelectField';
import { Label } from '@/components/UI/Label/Label';
import useDienstregelpunten from '@/state/selectors/useDienstregelpunten';
import type { LabeledEntryOrString } from '@/types/LabeledEntry';

import styles from './SignalenFilterDialog.module.css';
import inputFieldStyles from '@/components/UI/Fields/Fields.module.css';

export { defaultFilter };
export type { SignalenFilter };

const sortSignalenOptions = getSortSignalenOptions();
const defaultSortType = sortSignalenOptions[0];
const defaultValueSortDirection = SorteringRichting.DOWN;

const unmarshallFormData = (input: LabeledEntryOrString[]) => {
  if (input) {
    return input.map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      return item.value;
    });
  }
  return [];
};

const eenheidOfTreinNummerRegex = /^[\d]{3,14}$/; // NOSONAR
const eenheidOfTreinReeksRegex = /^([\d]{1,14}-)+[\d]{1,14}$/; // NOSONAR

const valideerEenheidOfTreinnummer: (value: string) => boolean | string = (value) => {
  return value === '' ||
    eenheidOfTreinNummerRegex.test(value) ||
    eenheidOfTreinReeksRegex.test(value) ||
    parseValidEnumeration(value)
    ? true
    : 'Invoer is geen geldige opsomming of reeks';
};

export type SignalenFilterDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  setNieuwFilter: (filter: SignalenFilter) => void;
  huidigFilter: SignalenFilter;
};

export type FilterSignalenFormProps = {
  eenheidOfTreinnummer: string;
  dienstregelpunten: LabeledEntryOrString[] | null;
  materieelTypen: LabeledEntryOrString[] | null;
  signaalTypen: LabeledEntryOrString[] | null;
  sorteringType: SorteringType;
  sorteringRichting: SorteringRichting;
};

const SignaalCheckboxItem: FC<{
  testId: string;
  label: string;
  onChange: (checked: boolean) => void;
  value: boolean;
}> = ({ testId, label, onChange, value }) => (
  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
    <Checkbox data-testid={testId} onChange={onChange} value={value} />
    <span className={styles.checkboxStyle} onClick={() => onChange(!value)} role='button'>
      {label}
    </span>
  </div>
);

export const SignalenFilterDialog = ({
  showDialog,
  handleOpenChange,
  huidigFilter,
  setNieuwFilter,
}: SignalenFilterDialogProps) => {
  const lijstVanDienstregelpunten = useDienstregelpunten();

  const { handleSubmit, reset, control, setValue } = useForm<FilterSignalenFormProps>({
    mode: 'onSubmit',
  });

  const { sorteringType: initialValueSortType, sorteringRichting: initialValueSortDirection } = useMemo(
    () => resolveSorting(huidigFilter, defaultFilter),
    [huidigFilter.sorteringRichting, huidigFilter.sorteringType],
  );

  const {
    handleSortChange,
    handleSortDirectionChange,
    selectedSortType,
    selectedSortDirection,
    setSelectedSortDirection,
    setSelectedSortType,
  } = useSorting(initialValueSortType, initialValueSortDirection);

  useEffect(() => {
    if (selectedSortDirection) {
      setValue('sorteringRichting', selectedSortDirection);
    }
  }, [selectedSortDirection, setValue]);

  useEffect(() => {
    if (selectedSortType) {
      setValue('sorteringType', selectedSortType);
    }
  }, [selectedSortType, setValue]);

  const createOption = (label: string) => ({ label, value: label });

  const initialValueSignaalTypen = useMemo(() => huidigFilter.signaalTypen ?? [], [huidigFilter.signaalTypen]);
  const { handleSignaalTypenChange, setSelectedSignaalTypen, selectedSignaalTypen, signaalTypenOptions } =
    useSignaalTypenSearch(initialValueSignaalTypen);

  // update de (default)value van signaaltypen in het form als de geselecteerde signaaltypen veranderen
  useEffect(() => {
    if (selectedSignaalTypen.length > 0) {
      setValue('signaalTypen', selectedSignaalTypen);
    }
  }, [selectedSignaalTypen, setValue]);

  const [isIngeregeldMat, setIsIngeregeldMat] = useState(huidigFilter.ingeregeldMat ?? false);
  const [isAlleenManco, setIsAlleenManco] = useState(huidigFilter.alleenManco ?? false);
  const [isAlleenWts, setIsAlleenWts] = useState(huidigFilter.alleenWts ?? false);

  const onSubmit = async (formData: FilterSignalenFormProps) => {
    const kandidaatFilter: SignalenFilter = {
      eenheidOfTreinnummer: formData.eenheidOfTreinnummer,
      dienstregelpunten:
        formData.dienstregelpunten && formData.dienstregelpunten.length != 0
          ? unmarshallFormData(formData.dienstregelpunten)
          : undefined,
      materieelTypen:
        formData.materieelTypen && formData.materieelTypen.length != 0
          ? unmarshallFormData(formData.materieelTypen)
          : undefined,
      signaalTypen:
        formData.signaalTypen && formData.signaalTypen.length != 0
          ? unmarshallFormData(formData.signaalTypen)
          : undefined,
      ingeregeldMat: isIngeregeldMat ? true : undefined,
      alleenManco: isAlleenManco ? true : undefined,
      alleenWts: isAlleenWts ? true : undefined,
      sorteringRichting: formData.sorteringRichting,
      sorteringType: formData.sorteringType,
    };
    if (
      Object.values(kandidaatFilter).every(
        (el) => el === undefined || el === '' || el === defaultValueSortDirection || el === defaultSortType.value,
      )
    ) {
      setNieuwFilter(defaultFilter);
    } else {
      setNieuwFilter(kandidaatFilter);
    }
    handleOpenChange(false);
  };

  return (
    <MwdDialog
      title='Filter signalen'
      className={styles.signalenFilterenDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid='filterSignalen'
      hasCloseInHeader
      headerSeparator
      paddingTopChildren={false}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SortField
          name='sorteringType'
          control={control}
          options={sortSignalenOptions}
          defaultValue={huidigFilter.sorteringType ?? defaultSortType.label}
          onChangeCallback={(value) => handleSortChange(value as SorteringType)}
          onSortDirectionChangeCallback={handleSortDirectionChange}
          selectedSortDirection={selectedSortDirection}
        />
        <div style={{ paddingBottom: '16px' }}>
          <InputField
            className={`${inputFieldStyles.inputField} ${styles.eenheidOfTreinnummerSignalenFilterInputStyle}`}
            label='Eenheid- of treinnummer'
            control={control}
            autoComplete='off'
            data-testid='eenheidOfTreinnummer'
            placeholder='Filter op nummer(s) of reeks'
            maxLength={255}
            name='eenheidOfTreinnummer'
            rules={{
              validate: {
                eenheidOfTreinnummer: valideerEenheidOfTreinnummer as Validate<
                  LabeledEntryOrString | LabeledEntryOrString[] | null,
                  FilterSignalenFormProps
                >,
              },
            }}
            showOptional={false}
            defaultValue={huidigFilter.eenheidOfTreinnummer ?? ''}
          />
        </div>
        <div>
          <DienstregelpuntSelectField
            control={control as Control<any>}
            name='dienstregelpunten'
            defaultValue={
              huidigFilter.dienstregelpunten ? huidigFilter.dienstregelpunten.map((item) => createOption(item)) : []
            }
            alleDrglptnamen={lijstVanDienstregelpunten}
          />
        </div>
        <MaterieelTypeSelectField
          name='materieelTypen'
          control={control as Control<any>}
          defaultValue={
            huidigFilter.materieelTypen ? huidigFilter.materieelTypen.map((item) => createOption(item)) : []
          }
        />
        <div>
          <CreatableSelectField
            label='Signaaltype'
            control={control as Control<any>}
            testId='signaalTypen'
            defaultValue={selectedSignaalTypen.map((item) => createOption(item))}
            createLabel='SignaalType:'
            enableSelectOneForMultiple={true}
            isMulti={true}
            isClearable
            isSearchable
            openMenuOnClick={true}
            name='signaalTypen'
            matchFromStart={true}
            options={signaalTypenOptions}
            onChangeCallback={handleSignaalTypenChange}
            placeholder='Voeg signaaltype(n) toe'
          />
        </div>
        <div>
          <Label>
            <div
              style={{ paddingTop: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}
            >
              Overige eigenschappen
            </div>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <SignaalCheckboxItem
              label='Alleen ingeregeld materieel'
              testId='ingeregeldMat'
              onChange={(isChecked) => setIsIngeregeldMat(isChecked)}
              value={isIngeregeldMat}
            />
            <SignaalCheckboxItem
              label='Alleen manco werklijnen'
              testId='mancoWerklijnen'
              onChange={(isChecked) => setIsAlleenManco(isChecked)}
              value={isAlleenManco}
            />
            <SignaalCheckboxItem
              label='Alleen uit WTS'
              testId='uitWTS'
              onChange={(isChecked) => setIsAlleenWts(isChecked)}
              value={isAlleenWts}
            />
          </div>
        </div>
        <MwdDialog.Footer
          submitCaption='Filter signalen'
          cancelCallback={() => {
            reset({
              eenheidOfTreinnummer: '',
              dienstregelpunten: null,
              materieelTypen: null,
              signaalTypen: null,
              sorteringType: defaultSortType.value,
              sorteringRichting: defaultValueSortDirection,
            });
            setSelectedSignaalTypen([]);
            setSelectedSortType(defaultSortType.value);
            setSelectedSortDirection(defaultValueSortDirection);
            setIsIngeregeldMat(false);
            setIsAlleenWts(false);
            setIsAlleenManco(false);
            setNieuwFilter(defaultFilter);
            handleOpenChange(false);
          }}
          cancelCaption='Verwijder criteria'
          justifyContentStyle='space-between'
        />
      </form>
    </MwdDialog>
  );
};
